import React, { useState, useEffect, useRef } from 'react';
import { Empty, Badge, Button } from 'antd';
import { MenuUnfoldOutlined, BellFilled } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Room } from '../types/chat';
import { Message } from '../types/chat';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { ConnectionStatus } from './chat/ConnectionStatus';
import notificationService from '../services/NotificationService';
import AppHeader from './chat/AppHeader';
import RoomsList from './chat/RoomsList';
import ChatHeader from './chat/ChatHeader';
import MessageList from './chat/MessageList';
import MessageInput from './chat/MessageInput';
import MembersList from './chat/MembersList';
import { notificationService as globalNotificationService } from '../services/NotificationService';
import { useWebSocket } from '../contexts/WebSocketProvider';

import './styles/ChatRooms.css';

// Флаг для включения/отключения отладочных сообщений
const DEBUG = false;

// Вспомогательные функции для логирования
const log = (message: string, ...args: any[]): void => {
  if (DEBUG) {
    console.log(message, ...args);
  }
};

interface ChatRoomsProps {
  rooms: Record<string, Room>;
  onRoomUpdate: (updater: (prevRooms: Record<string, Room>) => Record<string, Room>) => void;
  onLogout?: () => void;
  wsStatus?: ConnectionStatus;
  onReconnect?: () => void;
  onActiveRoomChange?: (roomId: string | null) => void;
  webSocket?: WebSocket | null;
}

const ChatRooms: React.FC<ChatRoomsProps> = ({ 
  rooms, 
  onRoomUpdate, 
  onLogout = () => {}, 
  wsStatus = 'disconnected',
  onReconnect = () => {},
  onActiveRoomChange,
  webSocket
}) => {
  const [activeRoomId, setActiveRoomId] = useState<string | null>(null);
  const [activeRoom, setActiveRoom] = useState<Room | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useLocalStorage<boolean>('sidebarCollapsed', false);
  const [membersSidebarCollapsed, setMembersSidebarCollapsed] = useLocalStorage<boolean>('membersSidebarCollapsed', false);
  const [replyToMessage, setReplyToMessage] = useState<Message | null>(null);
  const [editingMessage, setEditingMessage] = useState<Message | null>(null);
  const [highlightedMessageId, setHighlightedMessageId] = useState<string | null>(null);
  const { t } = useTranslation();
  
  // Получаем данные об онлайн участниках
  const { onlineMembers } = useWebSocket();
  
  // Функция для получения общего количества онлайн участников в текущей комнате
  const getOnlineMembersCount = (): number => {
    if (!onlineMembers || !activeRoomId) return 0;
    
    const roomData = onlineMembers.rooms[activeRoomId];
    if (!roomData) return 0;
    
    const internalOnline = roomData.internal_users.online.length;
    const matrixOnline = roomData.matrix_users.online.length;
    
    return internalOnline + matrixOnline;
  };
  
  // Refs для отслеживания взаимодействия с комнатой
  const messageInputRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  // Ref для хранения текущей активной комнаты
  const activeRoomIdRef = useRef<string | null>(null);
  
  // Обновляем ref при изменении activeRoomId
  useEffect(() => {
    activeRoomIdRef.current = activeRoomId;
  }, [activeRoomId]);
  
  // Set the first room as active by default if no room is selected
  useEffect(() => {
    if (!activeRoomId && rooms && Object.keys(rooms).length > 0) {
      const firstRoomId = Object.keys(rooms)[0];
      setActiveRoomId(firstRoomId);
      if (onActiveRoomChange) {
        onActiveRoomChange(firstRoomId);
      }
    }
  }, [rooms, activeRoomId, onActiveRoomChange]); // Add activeRoomId and onActiveRoomChange to dependencies
  
  // Отслеживаем взаимодействие с комнатой для сброса индикации
  useEffect(() => {
    const messageInput = messageInputRef.current;
    const messagesContainer = messagesContainerRef.current;
    
    if (!messageInput || !messagesContainer || !activeRoomId) return;
    
    // Функция для сброса индикации
    const handleInteraction = () => {
      // Сохраняем информацию о взаимодействии пользователя в localStorage
      localStorage.setItem('user_interacted_with_rooms', 'true');

      // Помечаем комнату как прочитанную только при явном взаимодействии
      if (activeRoomId && rooms[activeRoomId]?.hasNewMessage) {
        log('[DEBUG] User interaction detected in room:', activeRoomId, 'marking messages as read');
        
        onRoomUpdate((prevRooms) => {
          // Помечаем текущую комнату как прочитанную
          const updatedRooms = notificationService.markRoomAsRead(activeRoomId, prevRooms);
          
          // Проверяем, остались ли непрочитанные сообщения в других комнатах
          const hasUnreadMessages = notificationService.checkUnreadMessages(updatedRooms);
          log('[DEBUG] Has unread messages in other rooms after marking current room as read:', hasUnreadMessages);
          
          // Если все сообщения прочитаны, останавливаем уведомление в заголовке
          if (!hasUnreadMessages) {
            log('[DEBUG] No unread messages left, stopping title notification');
            notificationService.stopTitleNotification();
          } else {
            log('[DEBUG] There are still unread messages in other rooms, title will continue blinking');
          }
          
          return updatedRooms;
        });
      }
    };
    
    // Добавляем слушатели событий
    messageInput.addEventListener('click', handleInteraction);
    messageInput.addEventListener('focus', handleInteraction);
    messagesContainer.addEventListener('click', handleInteraction);
    
    return () => {
      messageInput.removeEventListener('click', handleInteraction);
      messageInput.removeEventListener('focus', handleInteraction);
      messagesContainer.removeEventListener('click', handleInteraction);
    };
  }, [activeRoomId, rooms, onRoomUpdate]);

  // Не сбрасываем индикацию автоматически при активации комнаты,
  // теперь это будет происходить только при явном взаимодействии пользователя

  // Set active room when changed
  useEffect(() => {
    if (activeRoomId && rooms[activeRoomId]) {
      setActiveRoom(rooms[activeRoomId]);
    } else {
      setActiveRoom(null);
    }
  }, [activeRoomId, rooms]); 

  // Handle room selection
  const handleRoomClick = (roomId: string) => {
    // Очищаем подсвечивание новых реакций при смене комнаты
    globalNotificationService.clearNewReactions();
    
    setActiveRoomId(roomId);
    if (onActiveRoomChange) {
      onActiveRoomChange(roomId);
    }
  };

  // Toggle sidebar
  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  // Toggle members sidebar
  const toggleMembersSidebar = () => {
    setMembersSidebarCollapsed(!membersSidebarCollapsed);
  };

  // Handle reply to message
  const handleReplyToMessage = (message: Message) => {
    setReplyToMessage(message);
  };

  // Cancel reply
  const handleCancelReply = () => {
    setReplyToMessage(null);
  };

  // Handle edit message
  const handleEditMessage = (message: Message) => {
    setEditingMessage(message);
    // Отменяем ответ, если был активен
    setReplyToMessage(null);
  };

  // Cancel edit
  const handleCancelEdit = () => {
    setEditingMessage(null);
  };

  // Handle mention user
  const handleMentionUser = (user: any) => {
    // Можно добавить дополнительную логику здесь если нужно
    console.log('Упоминание пользователя:', user);
  };

  // Handle jump to message from search
  const handleJumpToMessage = (eventId: string, contextMessages?: { before: any[], after: any[] }) => {
    // Если есть контекстные сообщения, вставляем их в историю чата
    if (contextMessages && activeRoomId && (contextMessages.before.length > 0 || contextMessages.after.length > 0)) {
      console.log('📝 Вставка контекстных сообщений:', {
        before: contextMessages.before.length,
        after: contextMessages.after.length,
        targetEventId: eventId
      });

      onRoomUpdate(prevRooms => {
        const updatedRooms = { ...prevRooms };
        const currentRoom = updatedRooms[activeRoomId];
        
        if (!currentRoom) return prevRooms;

        // Создаем копию текущих сообщений
        const currentMessages = [...(currentRoom.messages || [])];
        
        // Проверяем, есть ли уже найденное сообщение в истории
        const targetMessageIndex = currentMessages.findIndex(msg => msg.event_id === eventId);
        
        if (targetMessageIndex !== -1) {
          // Сообщение уже есть в истории, просто прокручиваем к нему
          console.log('✅ Целевое сообщение уже в истории, прокручиваем');
        } else {
          // Нужно вставить контекстные сообщения
          console.log('📥 Вставка новых контекстных сообщений в историю');
          
          // Собираем все сообщения для вставки (до, найденное, после)
          const messagesToInsert: Message[] = [];
          
          // Добавляем сообщения "до"
          contextMessages.before.forEach(msg => {
            // Проверяем, нет ли уже этого сообщения в истории
            if (!currentMessages.find(m => m.event_id === msg.event_id)) {
              messagesToInsert.push(msg as Message);
            }
          });
          
          // Добавляем целевое сообщение (если есть в контексте)
          const contextAny = contextMessages as any;
          if (contextAny.target && !currentMessages.find(m => m.event_id === contextAny.target.event_id)) {
            messagesToInsert.push(contextAny.target as Message);
          }
          
          // Добавляем сообщения "после"
          contextMessages.after.forEach(msg => {
            // Проверяем, нет ли уже этого сообщения в истории
            if (!currentMessages.find(m => m.event_id === msg.event_id)) {
              messagesToInsert.push(msg as Message);
            }
          });
          
          // Сортируем сообщения по времени создания
          messagesToInsert.sort((a, b) => {
            const aCreated = typeof a.created === 'number' ? a.created : 0;
            const bCreated = typeof b.created === 'number' ? b.created : 0;
            return aCreated - bCreated;
          });
          
          // Находим правильное место для вставки в существующую историю
          // Ищем первое сообщение в истории, которое новее чем наши новые сообщения
          let insertIndex = currentMessages.findIndex(msg => 
            (msg.created || 0) > (messagesToInsert[0]?.created || 0)
          );
          
          // Если не нашли (все наши сообщения новее), вставляем в конец
          if (insertIndex === -1) {
            insertIndex = currentMessages.length;
          }
          
          // Вставляем новые сообщения
          currentMessages.splice(insertIndex, 0, ...messagesToInsert);
          
          console.log(`✅ Вставлено ${messagesToInsert.length} новых сообщений на позицию ${insertIndex}`);
        }
        
        // Обновляем комнату с новыми сообщениями
        updatedRooms[activeRoomId] = {
          ...currentRoom,
          messages: currentMessages
        };
        
        return updatedRooms;
      });
    }
    
    // Подсвечиваем сообщение
    setHighlightedMessageId(eventId);
    
    // Убираем подсветку через 3 секунды
    setTimeout(() => {
      setHighlightedMessageId(null);
    }, 3000);
    
    // Прокручиваем к сообщению с улучшенной логикой
    setTimeout(() => {
      const messageElement = document.querySelector(`[data-event-id="${eventId}"]`);
      if (messageElement) {
        // Проверяем, является ли это последним сообщением
        const messagesContainer = messagesContainerRef.current;
        if (messagesContainer) {
          const containerRect = messagesContainer.getBoundingClientRect();
          const messageRect = messageElement.getBoundingClientRect();
          
          // Если сообщение находится в нижней части контейнера (последние сообщения),
          // используем 'end' вместо 'center' чтобы не нарушать верстку
          const isNearBottom = messageRect.bottom > containerRect.bottom - 100;
          
          messageElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: isNearBottom ? 'end' : 'center',
            inline: 'nearest'
          });
        } else {
          // Fallback к старому поведению если контейнер не найден
          messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      } else {
        console.warn('⚠️ Элемент сообщения не найден:', eventId);
      }
    }, 300); // Увеличиваем задержку для отрисовки новых сообщений
  };

  // Clear reply and edit when room changes
  useEffect(() => {
    setReplyToMessage(null);
    setEditingMessage(null);
  }, [activeRoomId]);

  // Проверяем, есть ли в комнатах непрочитанные сообщения
  const hasUnreadMessages = Object.values(rooms).some(room => room.hasNewMessage);

  return (
    <div className="chat-container">
      <AppHeader 
        onLogout={onLogout} 
        connectionStatus={wsStatus} 
        onReconnect={onReconnect} 
      />
      
      <div className="main-content">
        <div className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
          {!sidebarCollapsed ? (
            <RoomsList 
              rooms={rooms} 
              activeRoomId={activeRoomId} 
              onRoomSelect={handleRoomClick} 
              toggleSidebar={toggleSidebar} 
            />
          ) : (
            <div className="sidebar-collapsed-controls">
              <Badge 
                dot={hasUnreadMessages} 
                color="red" 
                status={hasUnreadMessages ? "processing" : "default"} 
                className="rooms-indicator"
              >
                <Button 
                  icon={<MenuUnfoldOutlined />} 
                  type="text" 
                  onClick={toggleSidebar}
                  className="sidebar-expand-button"
                />
              </Badge>
            </div>
          )}
        </div>
        
        <div className="chat-content">
          {activeRoom ? (
            <>
              <ChatHeader 
                room={activeRoom} 
                roomId={activeRoomId} 
                toggleMembersSidebar={toggleMembersSidebar} 
                membersSidebarCollapsed={membersSidebarCollapsed}
                onlineMembersCount={getOnlineMembersCount()}
                onJumpToMessage={handleJumpToMessage}
              />
              
              <div ref={messagesContainerRef} className="messages-wrapper">
                <MessageList 
                  room={activeRoom} 
                  onRoomUpdate={(roomId, messageUpdater) => {
                    onRoomUpdate(prevRooms => {
                      // Create a shallow copy of rooms
                      const updatedRooms = { ...prevRooms };
                      
                      // Update the specific room's messages
                      if (updatedRooms[roomId]) {
                        updatedRooms[roomId] = {
                          ...updatedRooms[roomId],
                          messages: messageUpdater(updatedRooms[roomId].messages)
                        };
                      }
                      
                      return updatedRooms;
                    });
                  }}
                  webSocket={webSocket}
                  onReplyMessage={handleReplyToMessage}
                  onEditMessage={handleEditMessage}
                  highlightedMessageId={highlightedMessageId}
                />
              </div>
              
              <div ref={messageInputRef} className="message-input-wrapper">
                <MessageInput 
                  roomId={activeRoomId!} 
                  replyingTo={replyToMessage}
                  onCancelReply={handleCancelReply}
                  editingMessage={editingMessage}
                  onCancelEdit={handleCancelEdit}
                  webSocket={webSocket}
                  onMentionUser={handleMentionUser}
                />
              </div>
            </>
          ) : (
            <div className="select-room-placeholder">
              <Empty 
                description={t('chat.selectRoom')}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            </div>
          )}
        </div>
        
        {activeRoom && !membersSidebarCollapsed && (
          <MembersList 
            members={activeRoom.members || []} 
            currentRoomId={activeRoomId}
            toggleMembersSidebar={toggleMembersSidebar}
            onMentionUser={handleMentionUser}
          />
        )}
      </div>
    </div>
  );
};

export default ChatRooms;
